import React from 'react';
import { Shield } from 'lucide-react';
import { Button } from '../../components/Button';
import type { User as FirebaseUser } from 'firebase/auth';

interface SettingsSecurityProps {
    user: FirebaseUser | null | undefined;
    onSecure: () => void;
    onLogin: () => void;
    onSignUp: () => void;
}

export const SettingsSecurity: React.FC<SettingsSecurityProps> = ({
    user,
    onSecure,
    onLogin,
    onSignUp
}) => {
    // Show "insecure" state if user is anonymous OR not logged in
    if (!user || user.isAnonymous) {
        return (
            <div className="rounded-xl bg-amber-500/10 p-4 border border-amber-500/20">
                <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-amber-700 dark:text-amber-300">
                            {user ? 'Your data is local-only' : 'Not logged in'}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            {user
                                ? 'Secure your account to access your data on other devices.'
                                : 'Log in to sync your data across devices.'
                            }
                        </p>
                        <div className="mt-3 flex gap-2">
                            {/* Only show "Secure Account" if they are actually an anonymous user with a UID to link */}
                            {user?.isAnonymous && (
                                <Button
                                    size="sm"
                                    className="bg-amber-600 hover:bg-amber-700 border-none"
                                    onClick={onSecure}
                                >
                                    Secure Account
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant={user?.isAnonymous ? "outline" : "primary"}
                                className={user?.isAnonymous
                                    ? "border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                                    : "bg-amber-600 hover:bg-amber-700 border-none"
                                }
                                onClick={onLogin}
                            >
                                Login
                            </Button>
                            {!user?.isAnonymous && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                                    onClick={onSignUp}
                                >
                                    Sign Up
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-green-500/10 p-4 border border-green-500/20">
            <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                    <p className="text-sm font-bold text-green-700 dark:text-green-300">Account Secured</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Your data is synced across devices using your email.
                    </p>
                </div>
            </div>
        </div>
    );
};
